import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Bell, Check, CheckCheck, X, Building2, Users, Bot, BookOpen, FileText,
  Award, Flame, Trophy, Target, GraduationCap, ClipboardCheck, Sparkles,
  Stethoscope, UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCpdEnabled, useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';

interface SystemNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  recipient_id: string | null;
  tenant_id: string | null;
  entity_type: string | null;
  entity_name: string | null;
  actor_name: string | null;
  is_read: boolean;
  created_at: string;
  source: 'system';
  tenants?: { name: string } | null;
}

interface ActivityNotification {
  id: string;
  type: string;
  title: string;
  description?: string;
  created_at: string;
  metadata?: Record<string, unknown>;
  source: 'activity';
  is_read: boolean;
}

type UnifiedNotification = SystemNotification | ActivityNotification;

// All activity types that should appear as notifications
const ACTIVITY_NOTIFICATION_TYPES = [
  'badge_earned', 'streak_milestone', 'goal_reached', 'cpd_earned',
  'simulation_completed', 'coaching_completed', 'quiz_completed',
  'pathway_assigned', 'pathway_completed', 'certificate_earned',
  'content_generated', 'assessment_completed',
];

const systemIcons: Record<string, React.ReactNode> = {
  agent_created: <Bot className="h-4 w-4 text-purple-500" />,
  agent_updated: <Bot className="h-4 w-4 text-blue-500" />,
  agent_deleted: <Bot className="h-4 w-4 text-red-500" />,
  user_created: <Users className="h-4 w-4 text-green-500" />,
  user_updated: <Users className="h-4 w-4 text-blue-500" />,
  user_invited: <UserPlus className="h-4 w-4 text-blue-500" />,
  company_created: <Building2 className="h-4 w-4 text-orange-500" />,
  company_updated: <Building2 className="h-4 w-4 text-blue-500" />,
  pathway_created: <BookOpen className="h-4 w-4 text-indigo-500" />,
  pathway_assigned: <BookOpen className="h-4 w-4 text-green-500" />,
  pathway_completed: <BookOpen className="h-4 w-4 text-emerald-500" />,
  rubric_created: <FileText className="h-4 w-4 text-teal-500" />,
  template_created: <FileText className="h-4 w-4 text-cyan-500" />,
  certificate_issued: <GraduationCap className="h-4 w-4 text-amber-500" />,
  tenant_activity: <Building2 className="h-4 w-4 text-gray-500" />,
};

const activityIcons: Record<string, React.ReactNode> = {
  badge_earned: <Award className="h-4 w-4 text-yellow-600" />,
  streak_milestone: <Flame className="h-4 w-4 text-orange-600" />,
  goal_reached: <Target className="h-4 w-4 text-blue-600" />,
  cpd_earned: <Trophy className="h-4 w-4 text-purple-600" />,
  simulation_completed: <Stethoscope className="h-4 w-4 text-green-600" />,
  coaching_completed: <Users className="h-4 w-4 text-cyan-600" />,
  quiz_completed: <ClipboardCheck className="h-4 w-4 text-indigo-600" />,
  pathway_assigned: <BookOpen className="h-4 w-4 text-blue-600" />,
  pathway_completed: <GraduationCap className="h-4 w-4 text-emerald-600" />,
  certificate_earned: <GraduationCap className="h-4 w-4 text-amber-600" />,
  content_generated: <Sparkles className="h-4 w-4 text-pink-600" />,
  assessment_completed: <ClipboardCheck className="h-4 w-4 text-teal-600" />,
};

export function UnifiedNotificationBell() {
  const { isMasterTenant } = useTenant();
  const cpdEnabled = useCpdEnabled();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'system' | 'activity'>('all');
  const activityNotificationTypes = useMemo(
    () => cpdEnabled
      ? ACTIVITY_NOTIFICATION_TYPES
      : ACTIVITY_NOTIFICATION_TYPES.filter((type) => type !== 'cpd_earned'),
    [cpdEnabled],
  );

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      const allNotifications: UnifiedNotification[] = [];
      const authId = user.supabaseAuthId || user.id;

      // Fetch system notifications
      // Admin sees all; regular users see only their targeted notifications
      const supabaseAny = supabase as any;
      let sysQuery = supabaseAny
        .from('notifications')
        .select('*, tenants:tenant_id (name)')
        .order('created_at', { ascending: false })
        .limit(30);

      if (!isMasterTenant || !user.roles?.includes('admin')) {
        // Non-admin: only see notifications targeted at them
        sysQuery = sysQuery.eq('recipient_id', authId);
      }

      const { data: systemData, error: systemError } = await sysQuery;

      if (!systemError && systemData) {
        allNotifications.push(...systemData.map((n: Record<string, unknown>) => ({
          ...n,
          source: 'system' as const,
        })));
      }

      // Fetch activity notifications (all users see their own)
      const { data: activityData, error: activityError } = await supabase
        .from('user_activity_log')
        .select('*')
        .eq('user_id', user.id)
        .in('activity_type', activityNotificationTypes)
        .order('created_at', { ascending: false })
        .limit(30);

      if (!activityError && activityData) {
        allNotifications.push(...activityData.map((item: Record<string, unknown>) => ({
          id: item.id as string,
          type: item.activity_type as string,
          title: item.title as string,
          description: (item.description as string) || undefined,
          created_at: item.created_at as string,
          metadata: item.metadata as Record<string, unknown>,
          source: 'activity' as const,
          is_read: (item.is_read as boolean) ?? false,
        })));
      }

      // Sort by created_at descending
      allNotifications.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setNotifications(allNotifications);
      setUnreadCount(allNotifications.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user, isMasterTenant, activityNotificationTypes]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refetch when popover opens
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const authId = user.supabaseAuthId || user.id;

    // Subscribe to new system notifications
    const systemChannel = supabase
      .channel('notifications-realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
      }, (payload) => {
        const newNotif = payload.new as Record<string, unknown>;
        // Only show if targeted at this user or user is admin
        const isTargeted = newNotif.recipient_id === authId;
        const isAdmin = isMasterTenant && user.roles?.includes('admin');
        if (isTargeted || isAdmin) {
          const notification: SystemNotification = {
            ...(newNotif as unknown as SystemNotification),
            source: 'system',
          };
          setNotifications(prev => [notification, ...prev]);
          setUnreadCount(prev => prev + 1);
        }
      })
      .subscribe();

    // Subscribe to new activity log entries for this user
    const activityChannel = supabase
      .channel('activity-realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_activity_log',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        const newItem = payload.new as Record<string, unknown>;
        if (activityNotificationTypes.includes(newItem.activity_type as string)) {
          const notification: ActivityNotification = {
            id: newItem.id as string,
            type: newItem.activity_type as string,
            title: newItem.title as string,
            description: (newItem.description as string) || undefined,
            created_at: newItem.created_at as string,
            metadata: newItem.metadata as Record<string, unknown>,
            source: 'activity',
            is_read: false,
          };
          setNotifications(prev => [notification, ...prev]);
          setUnreadCount(prev => prev + 1);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(systemChannel);
      supabase.removeChannel(activityChannel);
    };
  }, [user, isMasterTenant, activityNotificationTypes]);

  const markAsRead = async (notification: UnifiedNotification) => {
    if (notification.is_read) return;

    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      if (notification.source === 'system') {
        await supabase
          .from('notifications')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .eq('id', notification.id);
      } else {
        await supabase
          .from('user_activity_log')
          .update({ is_read: true })
          .eq('id', notification.id);
      }
    } catch (err) {
      // Revert on error
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, is_read: false } : n)
      );
      setUnreadCount(prev => prev + 1);
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.is_read);
    if (unreadNotifications.length === 0) return;

    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);

    try {
      const systemIds = unreadNotifications.filter(n => n.source === 'system').map(n => n.id);
      if (systemIds.length > 0) {
        await supabase
          .from('notifications')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .in('id', systemIds);
      }

      const activityIds = unreadNotifications.filter(n => n.source === 'activity').map(n => n.id);
      if (activityIds.length > 0) {
        await supabase
          .from('user_activity_log')
          .update({ is_read: true })
          .in('id', activityIds);
      }
    } catch (err) {
      fetchNotifications();
    }
  };

  const getIcon = (notification: UnifiedNotification) => {
    if (notification.source === 'system') {
      return systemIcons[notification.type] || <Bell className="h-4 w-4" />;
    }
    return activityIcons[notification.type] || <Bell className="h-4 w-4" />;
  };

  const getMessage = (notification: UnifiedNotification) => {
    if (notification.source === 'system') {
      return (notification as SystemNotification).message;
    }
    return (notification as ActivityNotification).description || notification.title;
  };

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'all') return true;
    if (activeTab === 'system') return n.source === 'system';
    if (activeTab === 'activity') return n.source === 'activity';
    return true;
  });

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs h-7"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'all' | 'system' | 'activity')} className="w-full">
          <TabsList className="w-full grid grid-cols-3 h-9 rounded-none border-b">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="system" className="text-xs">System</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs">Activity</TabsTrigger>
          </TabsList>
        </Tabs>

        <ScrollArea className="h-80">
          {loading ? (
            <div className="flex items-center justify-center h-20">
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-20 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <span className="text-sm">No notifications</span>
            </div>
          ) : (
            <div className="divide-y">
              {filteredNotifications.map((notification) => (
                <div
                  key={`${notification.source}-${notification.id}`}
                  className={`p-3 hover:bg-muted/50 cursor-pointer transition-colors ${
                    notification.is_read ? 'opacity-60' : ''
                  }`}
                  onClick={() => markAsRead(notification)}
                >
                  <div className="flex gap-3">
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      notification.is_read ? 'bg-muted' : 'bg-primary/10'
                    }`}>
                      {getIcon(notification)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium truncate ${
                          notification.is_read ? 'text-muted-foreground' : ''
                        }`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {getMessage(notification)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </span>
                        {notification.source === 'system' && (notification as SystemNotification).tenants?.name && (
                          <Badge variant="outline" className="text-xs h-4 px-1">
                            {(notification as SystemNotification).tenants?.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
